import type { RefObject } from 'react';
import { Platform } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { Button, ButtonText } from '@/components/ui/button';
import { HStack } from '@/components/ui/hstack';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';
import { VStack } from '@/components/ui/vstack';

type TourTooltipCardProps = {
  text: string;
  primaryLabel: string;
  onPrimary: () => void;
  /** Holds the primary button disabled until the reader has done what the step asks. */
  canAdvance?: boolean;
  /** Omitted on a tour's very last step, where there is nothing left to skip. */
  onSkip?: () => void;
  /** The skip button's label, for a tour too short to be "skipped". */
  skipLabel?: string;
  /** Omitted on a segment's first step. */
  onBack?: () => void;
  /** A caret on the card's edge facing its target: `down` when the card sits above it. */
  pointer?: 'up' | 'down';
  /** Takes screen reader focus when the step shows, so the step is announced where it appears. */
  textRef?: RefObject<unknown>;
};

/**
 * The strip beside the card that the caret sits in. The tour clips whatever
 * leaves the card's box, so the caret needs room inside it.
 */
const POINTER_GUTTER = { none: '', down: 'pb-2', up: 'pt-2' } as const;

/** Even iOS line breaks. The prop is iOS-only, and on web it would reach the DOM. */
const IOS_LINE_BREAKS =
  Platform.OS === 'ios' ? ({ lineBreakStrategyIOS: 'standard' } as const) : {};

/** The caret is a rotated square half over the card's edge, so only its tip shows. */
const POINTER_CARET = {
  down: 'bottom-[2px] border-b border-r',
  up: 'top-[2px] border-l border-t',
} as const;

/**
 * The card a guided tour shows beside its highlight. Each tour wraps it with
 * its own step logic, so every tour looks and reads the same.
 *
 * The card carries its own edge because the tour's scrim dims the page to
 * within a shade of the card in dark mode, where the card is the darkest
 * surface in the theme. The edge is `outline-300` in dark, the lightest stop
 * that clears 3:1 against that scrim. Lifting the card to a lighter surface
 * instead drops the buttons' muted labels below AA.
 */
export function TourTooltipCard({
  text,
  primaryLabel,
  onPrimary,
  canAdvance = true,
  onSkip,
  skipLabel = 'Skip tour',
  onBack,
  pointer,
  textRef,
}: TourTooltipCardProps) {
  return (
    <View className={twJoin('relative w-full max-w-md', POINTER_GUTTER[pointer ?? 'none'])}>
      <VStack className="w-full gap-3 rounded-2xl border border-outline-100 bg-background-0 p-4 shadow-soft-3 dark:border-outline-300">
        {/* `standard` evens out iOS line breaks. It does not stop a last word sitting alone, so keep that in the copy with a non-breaking space. */}
        <Text
          ref={textRef as RefObject<never>}
          className="text-base leading-6 text-typography-900"
          {...IOS_LINE_BREAKS}
          accessibilityLiveRegion="polite">
          {text}
        </Text>

        <HStack className="flex-wrap items-center justify-between gap-2">
          {onSkip ? (
            <Button variant="outline" onPress={onSkip} className="h-12">
              <ButtonText>{skipLabel}</ButtonText>
            </Button>
          ) : (
            <VStack />
          )}

          <HStack className="ml-auto gap-2">
            {onBack ? (
              <Button variant="outline" action="secondary" onPress={onBack} className="h-12">
                <ButtonText>Back</ButtonText>
              </Button>
            ) : null}

            <Button onPress={onPrimary} isDisabled={!canAdvance} className="h-12">
              <ButtonText>{primaryLabel}</ButtonText>
            </Button>
          </HStack>
        </HStack>
      </VStack>

      {pointer ? (
        <View
          pointerEvents="none"
          aria-hidden
          className={twJoin(
            'absolute left-8 h-3 w-3 rotate-45 border-outline-100 bg-background-0 dark:border-outline-300',
            POINTER_CARET[pointer]
          )}
        />
      ) : null}
    </View>
  );
}
